import type { FC } from "react"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import type { DateTime } from "luxon"
import { DateTime as LuxonDateTime } from "luxon"
import { Stack, Tooltip, IconButton, useTheme } from "@mui/material"
import { useTranslation } from "react-i18next"

import ZoomOutIcon from "icons/ZoomOutIcon"

import type { EntityType } from "types/common.types"
import type { DateRange, DateRangeDateTime } from "types/date.types"

import useSelectOptions from "helpers/hooks/useSelectOptions"
import { DatePickerSectionCard } from "widgets/styled/containers"
import {
  setInitialDateRange,
  setRangeDateRange,
  initializePicker,
  formatDateRange,
  setCustomDateRange,
  exitZoomMode,
} from "./helpers"
import DateRangeCustomSelect from "./DateRangeCustomSelect"

interface DateRangePickerProps {
  dateRange: DateRange | null
  dispatchDateRange: (dateRange: DateRange) => void
  isFetching?: boolean
  entity?: EntityType
}

const DateRangePicker: FC<DateRangePickerProps> = ({
  dateRange,
  dispatchDateRange,
  isFetching = false,
  entity = "device",
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [searchParams, setSearchParams] = useSearchParams()
  const dispatchCombo = useMemo(() => {
    return { setSearchParams, dispatchDateRange }
  }, [dispatchDateRange, setSearchParams])

  const { getDateReferencesByEntity } = useSelectOptions()
  const dateReferences = useMemo(
    () => getDateReferencesByEntity(entity),
    [entity, getDateReferencesByEntity],
  )

  // read url params only on first render, ignore updates made by ourselves
  const { initialDateRange } = useMemo(
    () => initializePicker(searchParams, dateRange, dateReferences, entity),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateReferences],
  )

  useEffect(() => {
    // Set initial redux value
    setInitialDateRange(initialDateRange, dispatchCombo)
    // This is only updated after the first render.
    // Intentionally not re-evaluating on update.

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dateRangeDateTime = useMemo(
    () => formatDateRange(dateRange),
    // Value from redux, this is the one used in the component
    [dateRange],
  )

  const updateDateRange = useCallback(
    (fields: { [key: string]: DateTime | null }, dateRange: DateRangeDateTime) => {
      if (dateRange) {
        setCustomDateRange(fields, dateRange, dispatchCombo)
      }
    },
    [dispatchCombo],
  )

  const updateDateReference = useCallback(
    (dateReferenceName: string) => {
      setRangeDateRange(dateReferenceName, dateReferences, dispatchCombo)
    },
    [dateReferences, dispatchCombo],
  )

  const handleExitZoomMode = useCallback(() => {
    if (dateRangeDateTime) {
      exitZoomMode(dateRangeDateTime, dateReferences, dispatchCombo)
    }
  }, [dateRangeDateTime, dateReferences, dispatchCombo])

  // Cleanup: exit zoom mode when component unmounts
  // Use ref to avoid stale closure and ensure we use latest values
  const cleanupRef = useRef({ dateRange, dateReferences, dispatchDateRange })
  cleanupRef.current = { dateRange, dateReferences, dispatchDateRange }

  useEffect(() => {
    return () => {
      const {
        dateRange: currentDateRange,
        dateReferences: refs,
        dispatchDateRange: dispatch,
      } = cleanupRef.current
      if (currentDateRange?.zoom) {
        // Exit zoom mode on unmount: only update Redux, don't update search params
        // This prevents navigation issues when leaving the page
        const { range } = currentDateRange

        // If custom, just set zoom to false
        if (range === "custom") {
          dispatch({ ...currentDateRange, zoom: false })
          return
        }

        // For other ranges, recalculate with now
        const dateReference = refs[range]
        if (dateReference) {
          const dateNow = LuxonDateTime.now()
          const fromDate = dateNow
            .minus({ [dateReference.timeUnit]: dateReference.timeAmount })
            .toISO() as string
          const toDate =
            dateReference.forecastTimeAmount && dateReference.forecastTimeUnit
              ? (dateNow
                  .plus({
                    [dateReference.forecastTimeUnit]: dateReference.forecastTimeAmount,
                  })
                  .toISO() as string)
              : (dateNow.toISO() as string)

          dispatch({
            fromDate,
            toDate,
            range,
            zoom: false,
            forecastThresholdDate: String(dateNow.toISO()),
          })
        }
      }
    }
    // Only run on unmount
  }, [])

  return (
    <>
      {dateRangeDateTime && (
        <>
          <DatePickerSectionCard>
            <Stack direction="row" spacing={1} alignItems="center">
              {dateRangeDateTime.zoom && (
                <Tooltip title={t("dates.EXIT_ZOOM_MODE")} arrow>
                  <IconButton
                    onClick={handleExitZoomMode}
                    aria-label="exit-zoom-mode"
                    color="primary"
                    sx={{
                      border: "1px solid",
                      borderRadius: "4px",
                      padding: "5px",
                    }}
                  >
                    <ZoomOutIcon color={theme.palette.primary.main} />
                  </IconButton>
                </Tooltip>
              )}
              <DateRangeCustomSelect
                dateRangeDateTime={dateRangeDateTime}
                updateDateRange={updateDateRange}
                setSelectedDateReference={updateDateReference}
                isFetching={isFetching}
                entity={entity}
              />
            </Stack>
          </DatePickerSectionCard>
        </>
      )}
    </>
  )
}

export default DateRangePicker
